// src/components/SendWindowPicker.jsx
// Shared component for the per-campaign send window: start/end time, days of
// week, IANA timezone. Used by both the outreach (Campaigns.jsx) and bulk
// (SendViaExcel.jsx) flows.
//
// Controlled component. Parent owns the value object:
//   { sendWindowStart, sendWindowEnd, sendWindowDays, sendWindowTimezone }
// All four fields must be set together for the backend to enforce the window.
// `value` may be null/undefined → component shows the "Add send window" toggle.
//
// Design choices (per the approved plan):
// - Timezone is REQUIRED — no implicit default. We surface a "Use my account
//   timezone" link as a convenience but the user must click to apply it.
// - Day order is Mon..Sun (ISO-friendly visual), but values map to JS Date
//   conventions: 0 = Sunday … 6 = Saturday — matches the backend.
import React, { useMemo } from 'react';

const DAY_LABELS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

function listTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch {}
  return [
    'UTC',
    'America/New_York',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Berlin',
    'Asia/Kolkata',
    'Asia/Dubai',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
  ];
}

function detectBrowserTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export default function SendWindowPicker({
  value,
  onChange,
  accountTimezone,
}) {
  const enabled = !!(value && (value.sendWindowStart || value.sendWindowEnd ||
    (value.sendWindowDays && value.sendWindowDays.length) || value.sendWindowTimezone));

  const timezones = useMemo(() => listTimezones(), []);

  const v = {
    sendWindowStart: value?.sendWindowStart || '',
    sendWindowEnd: value?.sendWindowEnd || '',
    sendWindowDays: Array.isArray(value?.sendWindowDays) ? value.sendWindowDays : [],
    sendWindowTimezone: value?.sendWindowTimezone || '',
  };

  const update = (patch) => onChange({ ...v, ...patch });

  const toggleEnabled = () => {
    if (enabled) {
      onChange({ sendWindowStart: '', sendWindowEnd: '', sendWindowDays: [], sendWindowTimezone: '' });
    } else {
      onChange({ sendWindowStart: '09:00', sendWindowEnd: '18:00', sendWindowDays: [1, 2, 3, 4, 5], sendWindowTimezone: '' });
    }
  };

  const toggleDay = (day) => {
    const current = new Set(v.sendWindowDays);
    if (current.has(day)) current.delete(day);
    else current.add(day);
    update({ sendWindowDays: [...current].sort((a, b) => a - b) });
  };

  const startValid = v.sendWindowStart === '' || HHMM_RE.test(v.sendWindowStart);
  const endValid = v.sendWindowEnd === '' || HHMM_RE.test(v.sendWindowEnd);
  const rangeValid =
    !v.sendWindowStart || !v.sendWindowEnd || v.sendWindowStart < v.sendWindowEnd;
  const daysValid = v.sendWindowDays.length > 0;
  const tzValid = !!v.sendWindowTimezone;

  const allValid = startValid && endValid && rangeValid && daysValid && tzValid;
  const showRangeError = enabled && v.sendWindowStart && v.sendWindowEnd && !rangeValid;

  return (
    <div className="p-4 my-3 border border-stroke rounded-lg bg-surface">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="font-semibold text-sm">Send window</h4>
          <p className="text-xs text-ink-secondary">
            Restrict messages to specific days and hours in a chosen timezone.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={toggleEnabled}
            className="rounded"
          />
          <span>{enabled ? 'On' : 'Off'}</span>
        </label>
      </div>

      {enabled && (
        <div className="space-y-3 mt-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium mb-1">Start time</label>
              <input
                type="time"
                value={v.sendWindowStart}
                onChange={(e) => update({ sendWindowStart: e.target.value })}
                className={`w-full px-2 py-1.5 text-sm border rounded ${startValid ? 'border-stroke' : 'border-red-400'}`}
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium mb-1">End time</label>
              <input
                type="time"
                value={v.sendWindowEnd}
                onChange={(e) => update({ sendWindowEnd: e.target.value })}
                className={`w-full px-2 py-1.5 text-sm border rounded ${endValid ? 'border-stroke' : 'border-red-400'}`}
              />
            </div>
          </div>
          {showRangeError && (
            <p className="text-xs text-red-600">End time must be after start time.</p>
          )}

          <div>
            <label className="block text-xs font-medium mb-1">Allowed days</label>
            <div className="flex flex-wrap gap-1">
              {DAY_LABELS.map((d) => {
                const active = v.sendWindowDays.includes(d.value);
                return (
                  <button
                    type="button"
                    key={d.value}
                    onClick={() => toggleDay(d.value)}
                    className={`px-3 py-1.5 text-sm rounded border ${
                      active
                        ? 'bg-brand-600 text-white border-brand-600'
                        : 'bg-white text-ink-primary border-stroke hover:bg-surface-hover'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
            {!daysValid && (
              <p className="text-xs text-red-600 mt-1">Pick at least one day.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">
              Timezone <span className="text-red-600">*</span>
            </label>
            <select
              value={v.sendWindowTimezone}
              onChange={(e) => update({ sendWindowTimezone: e.target.value })}
              className={`w-full px-2 py-1.5 text-sm border rounded ${tzValid ? 'border-stroke' : 'border-red-400'}`}
            >
              <option value="">— Select a timezone —</option>
              {timezones.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-3 mt-1 text-xs text-ink-secondary">
              {accountTimezone && (
                <button
                  type="button"
                  className="text-sky-600 hover:underline"
                  onClick={() => update({ sendWindowTimezone: accountTimezone })}
                >
                  Use account timezone ({accountTimezone})
                </button>
              )}
              <button
                type="button"
                className="text-sky-600 hover:underline"
                onClick={() => update({ sendWindowTimezone: detectBrowserTz() })}
              >
                Use browser timezone ({detectBrowserTz()})
              </button>
            </div>
          </div>

          {!allValid && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
              All four fields must be filled before the campaign can be saved.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Helper: returns true if the window block on a campaign payload is fully
// specified (so the form can block submission). Keeps the validation logic in
// one place — parent forms just call this before POST.
export function isSendWindowComplete(value) {
  if (!value) return true; // disabled
  const allEmpty =
    !value.sendWindowStart &&
    !value.sendWindowEnd &&
    (!Array.isArray(value.sendWindowDays) || value.sendWindowDays.length === 0) &&
    !value.sendWindowTimezone;
  if (allEmpty) return true; // disabled state
  // Anything set → ALL must be set
  return (
    HHMM_RE.test(value.sendWindowStart || '') &&
    HHMM_RE.test(value.sendWindowEnd || '') &&
    Array.isArray(value.sendWindowDays) &&
    value.sendWindowDays.length > 0 &&
    !!value.sendWindowTimezone &&
    value.sendWindowStart < value.sendWindowEnd
  );
}
